import { motion } from 'framer-motion'
import SectionWrapper from '../layout/SectionWrapper'
import { challenges, pillars } from '../../data/timeline'

export default function EpreuvesSection() {
  return (
    <SectionWrapper
      id="epreuves"
      gradient="from-dark-secondary via-dark-primary to-dark-secondary"
    >
      {/* Header */}
      <motion.div className="text-center mb-16">
        <motion.span
          className="tag-pill mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
        >
          <span>💪</span>
          <span>Les Épreuves</span>
        </motion.span>

        <motion.h2
          className="text-4xl md:text-6xl font-serif font-bold gradient-text mb-4"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.2 }}
        >
          Ce Que Nous Avons Traversé
        </motion.h2>

        <motion.p
          className="text-lg text-light-pink-100/70 max-w-xl mx-auto"
          initial={{ opacity: 0 }}
          whileInView={{ opacity: 1 }}
          viewport={{ once: true }}
          transition={{ delay: 0.4 }}
        >
          Les moments difficiles nous ont rendus plus forts
        </motion.p>
      </motion.div>

      {/* Challenges */}
      <div className="grid md:grid-cols-2 gap-6 mb-16">
        {challenges.map((challenge, index) => (
          <motion.div
            key={challenge.id}
            className="glass-card border-l-4 border-primary-pink/50"
            initial={{ opacity: 0, x: index % 2 === 0 ? -30 : 30 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ delay: index * 0.1 }}
          >
            <div className="flex items-start gap-4">
              <span className="text-3xl">{challenge.emoji}</span>
              <div>
                <h3 className="text-lg font-bold text-white mb-1">
                  {challenge.title}
                </h3>
                <p className="text-light-pink-100/70 text-sm">
                  {challenge.description}
                </p>
              </div>
            </div>
          </motion.div>
        ))}
      </div>

      {/* Transition - Arrow Down */}
      <motion.div
        className="text-center my-12"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <motion.div
          className="inline-block"
          animate={{ y: [0, 10, 0] }}
          transition={{ duration: 2, repeat: Infinity }}
        >
          <span className="text-4xl">⬇️</span>
        </motion.div>
        <p className="text-primary-pink font-medium mt-4">
          Ce qui nous a rendus plus forts
        </p>
      </motion.div>

      {/* Pillars */}
      <motion.div
        className="max-w-4xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <h3 className="text-2xl font-serif font-bold text-center text-white mb-8">
          🏛️ Nos Trois Piliers
        </h3>

        <div className="grid md:grid-cols-3 gap-6">
          {pillars.map((pillar, index) => (
            <motion.div
              key={pillar.id}
              className={`glass-card text-center bg-gradient-to-br ${pillar.color} bg-opacity-10`}
              initial={{ opacity: 0, y: 50 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.2 }}
              whileHover={{
                scale: 1.05,
                boxShadow: '0 20px 40px rgba(232, 81, 178, 0.2)'
              }}
            >
              <motion.span
                className="text-5xl block mb-4"
                animate={{ scale: [1, 1.1, 1] }}
                transition={{ duration: 2, repeat: Infinity, delay: index * 0.3 }}
              >
                {pillar.emoji}
              </motion.span>
              <h4 className="text-xl font-bold text-white mb-2">{pillar.title}</h4>
              <p className="text-light-pink-100/70 text-sm">{pillar.description}</p>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Lessons Learned */}
      <motion.div
        className="mt-16 max-w-3xl mx-auto"
        initial={{ opacity: 0 }}
        whileInView={{ opacity: 1 }}
        viewport={{ once: true }}
      >
        <h3 className="text-xl font-serif font-bold text-center text-white mb-6">
          📚 Ce Que 2025 Nous a Appris
        </h3>

        <div className="grid sm:grid-cols-2 gap-4">
          {[
            { emoji: '🗣️', lesson: "L'importance de la communication" },
            { emoji: '🙏', lesson: 'La force de la prière ensemble' },
            { emoji: '🤝', lesson: 'Le soutien inconditionnel' },
            { emoji: '💝', lesson: "L'amour véritable se construit" }
          ].map((item, index) => (
            <motion.div
              key={index}
              className="flex items-center gap-3 px-4 py-3 bg-dark-primary/50 rounded-xl"
              initial={{ opacity: 0, x: -20 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ delay: index * 0.1 }}
            >
              <span className="text-2xl">{item.emoji}</span>
              <span className="text-light-pink-100/80">{item.lesson}</span>
            </motion.div>
          ))}
        </div>
      </motion.div>

      {/* Quote */}
      <motion.blockquote
        className="text-center mt-16 max-w-2xl mx-auto"
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
      >
        <p className="text-xl md:text-2xl text-light-pink-100/80 italic font-serif leading-relaxed">
          "Ce qui ne nous tue pas nous rend forts
          <br />
          Notre amour est notre trésor"
        </p>
        <footer className="mt-4 text-primary-pink text-sm">
          — Bridge
        </footer>
      </motion.blockquote>
    </SectionWrapper>
  )
}
